import { FiMapPin } from "react-icons/fi";

import type { PastProject } from "newui/features/Company/types";
import { PROFILE_TYPE } from "../../types";

interface MobileProjectRowProps {
	project: PastProject;
	profileTypeId: number;
	onClick: () => void;
}

/**
 * Compact card view of a project for mobile screens.
 * Used in: ProjectsExperienceTab table rows, KeyPersonnelDetailModal project rows.
 */
export const MobileProjectRow: React.FC<MobileProjectRowProps> = ({
	project,
	profileTypeId,
	onClick,
}) => {
	const isSubcontractor = profileTypeId === PROFILE_TYPE.TRADE;

	const valueLabel = isSubcontractor ? "Trade Value" : "Value";
	const valueDisplay = isSubcontractor
		? project.trade_value
		: project.value_humanized;

	const procurementDisplay = isSubcontractor
		? project.trades?.map((t: { name: string }) => t.name).join(", ")
		: project.procurement_methods?.map((p) => p.name).join(", ");

	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition-shadow hover:shadow-md"
		>
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0 flex-1">
					<h4 className="truncate font-roboto text-sm font-semibold text-gray-900">
						{project.name}
					</h4>
					{project.client_name && (
						<p className="mt-0.5 text-xs text-gray-500">
							{project.client_name}
						</p>
					)}
				</div>
				{project.year_completed && (
					<span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
						{project.year_completed}
					</span>
				)}
			</div>

			{/* Sectors */}
			{project.sectors && project.sectors.length > 0 && (
				<div className="mt-2 flex flex-wrap gap-1">
					{project.sectors.slice(0, 3).map((sector) => (
						<span
							key={sector.id}
							className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700"
						>
							{sector.name}
						</span>
					))}
				</div>
			)}

			{/* Key figures */}
			<div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
				{valueDisplay && (
					<span>
						<span className="font-medium text-gray-700">{valueLabel}:</span>{" "}
						{valueDisplay}
					</span>
				)}
				{procurementDisplay && (
					<span className="truncate">
						{procurementDisplay}
					</span>
				)}
			</div>

			{/* Location */}
			{project.location?.address && (
				<div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
					<FiMapPin size={10} />
					<span className="truncate">{project.location.address}</span>
				</div>
			)}
		</button>
	);
};
